import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Col, Input, Popconfirm, Row, Table, TablePaginationConfig } from 'antd';
import { OrganizationsApi } from 'api/api';
import { OrganizationDto } from 'api/models';
import { Button } from 'components/buttons/buttons';
import { Cards } from 'components/cards/frame/cards-frame';
import { PageHeader } from 'components/page-headers/page-headers';
import { CardToolbox, Main, TableWrapper } from 'container/styled';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { hasPermission } from 'utility/accessibility/hasPermission';
import FeatherIcon from 'feather-icons-react';
import { OrganizationForm } from './OrganizationForm';
import openNotificationWithIcon from 'utility/notification';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';

const organizationsApi = new OrganizationsApi();

type ModalState = {
  visible: boolean;
  editMode: boolean;
  organizationToEdit: OrganizationDto | null;
};

export const OrganizationsPage: React.FC = () => {
  const { t } = useTranslation();
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OrganizationDto[]>([]);
  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    editMode: false,
    organizationToEdit: null,
  });
  const [query, setQuery] = useState({
    searchTerm: '',
    pageNumber: 1,
    pageSize: 10,
  });
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await organizationsApi.apiOrganizationsGet(query);
      setData(response.data.items || []);
      setPagination({
        ...pagination,
        current: query.pageNumber,
        total: response.data.totalCount,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleSearch = (value: string) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      setQuery(prev => ({ ...prev, searchTerm: value, pageNumber: 1 }));
    }, 300);
  };

  const columns = [
    {
      title: t('organizations:name', 'Name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('organizations:email', 'Email'),
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: t('organizations:phone', 'Phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: t('organizations:projects', 'Projects'),
      dataIndex: 'projectsCount',
      key: 'projects',
    },
    {
      title: t('organizations:users', 'Users'),
      dataIndex: 'usersCount',
      key: 'users',
    },
    {
      title: t('organizations:status', 'Status'),
      key: 'status',
      render: (record: OrganizationDto) => (
        <span className={`status-badge ${record.isActive ? 'active' : 'inactive'}`}>
          {record.isActive ? t('global:active', 'Active') : t('global:inactive', 'Inactive')}
        </span>
      ),
    },
    {
      title: t('global:actions', 'Actions'),
      key: 'action',
      width: 120,
      render: (record: OrganizationDto) => (
        <div className="table-actions">
          {hasPermission('organizations:edit') && (
            <Button className="btn-icon" type="primary" shape="circle" onClick={() => handleEdit(record)}>
              <FeatherIcon icon="edit" size={16} />
            </Button>
          )}
          {hasPermission('organizations:deactivate') && (
            <Popconfirm
              title={t('organizations:delete-confirm', 'Are you sure you want to delete this organization?')}
              onConfirm={() => handleDelete(record.id as number)}
              okText={t('global:yes', 'Yes')}
              cancelText={t('global:no', 'No')}
            >
              <Button className="btn-icon" type="danger" shape="circle">
                <FeatherIcon icon="trash-2" size={16} />
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  const handleEdit = (organization: OrganizationDto) => {
    setModalState({
      visible: true,
      editMode: true,
      organizationToEdit: organization,
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await organizationsApi.apiOrganizationsIdDelete({ id });
      openNotificationWithIcon('success', t('organizations:deleted', 'Organization deleted successfully'));
      fetchOrganizations();
    } catch (err) {
      openNotificationWithIcon('error', t('organizations:delete-error', 'Failed to delete organization'));
    }
  };

  const handleModalSubmit = async () => {
    setModalState({
      visible: false,
      editMode: false,
      organizationToEdit: null,
    });
    await fetchOrganizations();
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t('organizations:title', 'Organizations')}
          buttons={[
            <ExportButtonPageHeader
              key="1"
              title={t('organizations:title', 'Organizations')}
              data={data}
              columns={['name', 'email', 'phoneNumber']}
            />,
            hasPermission('organizations:create') && (
              <Button
                key="2"
                type="primary"
                onClick={() => setModalState({ visible: true, editMode: false, organizationToEdit: null })}
              >
                {t('organizations:create', 'Add Organization')}
              </Button>
            ),
          ]}
        />
      </CardToolbox>

      <Main>
        <Row gutter={15}>
          <Col md={24}>
            <Cards headless>
              <div className="table-search-box">
                <Input.Search
                  placeholder={t('organizations:search', 'Search organizations...')}
                  onSearch={handleSearch}
                  style={{ width: 300, marginBottom: 16 }}
                />
              </div>
              <TableWrapper>
                <Table
                  loading={loading}
                  dataSource={data}
                  columns={columns}
                  pagination={{
                    current: query.pageNumber,
                    pageSize: query.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: [10, 50, 100, 1000],
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} ${t('global.of', 'of')} ${total} ${t('global.items', 'items')}`,
                    onChange: (pageNumber, pageSize) => setQuery(prev => ({ ...prev, pageNumber, pageSize })),
                  }}
                />
              </TableWrapper>
            </Cards>
          </Col>
        </Row>
      </Main>

      {modalState.visible && (
        <OrganizationForm
          visible={modalState.visible}
          editMode={modalState.editMode}
          organization={modalState.organizationToEdit}
          onCancel={() => setModalState({ visible: false, editMode: false, organizationToEdit: null })}
          onSuccess={handleModalSubmit}
        />
      )}
    </>
  );
};
